// THE ROUTE CONTRACT — one parser, one destination table, one navigator.
//
// Hash routing, because there is no server-side router and the console is one bundle. Two
// laws, both learned in Signet:
//
//   1. A PARAM NEVER REMOUNTS A PAGE. `key` is the page id alone, so a tab routing a filter
//      into a page does not destroy that page's own state. THE EXCEPTION, deliberate: the three
//      detail routes keep a composite key (`person:7`), because two people are two subjects,
//      not one page with a filter — and two runs are two conversations, each with its own
//      polling cursor over its own steps (BUILD-BRIEF-R3f §2).
//
//   2. PARAMS ARE ONE-SHOT ARRIVAL INTENTS. The receiving page applies them once through
//      `useDeepLink` and the query is then cleared, so (a) the page owns its filters
//      afterwards, (b) re-clicking the same link fires again, (c) back/forward never replays a
//      stale filter. Unknown params are ignored, never guessed at.
//
// This module is DEPENDENCY-FREE at runtime — no components, no registry import, no `window`
// at module scope — so it can be tested without booting the page registry.
import { createContext, useContext, useEffect, useRef } from 'react';
import type { PageId } from './pages/registry.js';

/** One resolved destination. Built only by a `ROUTES.*` constructor, never by concatenation. */
export interface Dest {
  page: PageId;
  /** One-shot arrival intents, already stringified; empty values are dropped. */
  params: Record<string, string>;
  /** The person detail route (`#person?id=N`). */
  personId?: number;
  /** The organisation detail route (`#org?id=N`). */
  organisationId?: number;
  /** The run desk (`#run?id=N`) — round 3f's third detail route. */
  runId?: number;
}

/** The parsed current location. */
export interface Route {
  page: PageId;
  personId: number | null;
  organisationId: number | null;
  runId: number | null;
  params: URLSearchParams;
  /** What the scroll reset watches — see law 1. */
  key: string;
}

const put = (out: Record<string, string>, key: string, value: string | number | boolean | null | undefined): void => {
  // An absent intent and an empty intent are the same thing. `false` and `0` are real values.
  if (value === null || value === undefined || value === '') return;
  out[key] = String(value);
};

const build = (page: PageId, spec: Record<string, string | number | boolean | null | undefined>): Dest => {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(spec)) put(params, k, v);
  return { page, params };
};

/**
 * THE DESTINATION TABLE. Where a destination is named anywhere in this console, it is named
 * here. A page that wants to be routed to grows a constructor; a caller never writes a hash.
 */
export const ROUTES = {
  today: (o: { anchor?: string | null } = {}): Dest => build('today', { anchor: o.anchor }),

  review: (o: { status?: string | null; kind?: string | null; principal?: string | null; proposal?: number | null } = {}): Dest =>
    build('review', { status: o.status, kind: o.kind, principal: o.principal, proposal: o.proposal }),

  inbox: (): Dest => build('inbox', {}),

  people: (o: { q?: string | null; tier?: string | null; status?: string | null; tree_of?: number | null } = {}): Dest =>
    build('people', { q: o.q, tier: o.tier, status: o.status, tree_of: o.tree_of }),

  /** The person detail route. `id` is not an arrival intent — it IS the address. */
  person: (o: { id: number; anchor?: string | null }): Dest => ({
    page: 'person',
    personId: o.id,
    params: o.anchor ? { anchor: o.anchor } : {},
  }),

  orgs: (o: { q?: string | null; status?: string | null } = {}): Dest => build('orgs', { q: o.q, status: o.status }),

  /** The organisation detail route. */
  org: (o: { id: number; anchor?: string | null }): Dest => ({
    page: 'org',
    organisationId: o.id,
    params: o.anchor ? { anchor: o.anchor } : {},
  }),

  pipeline: (o: { stage?: string | null; opportunity?: number | null } = {}): Dest =>
    build('pipeline', { stage: o.stage, opportunity: o.opportunity }),

  /** The personal index. `q` is an arrival intent: the page runs it once and then owns it. */
  search: (o: { q?: string | null } = {}): Dest => build('search', { q: o.q }),

  /** The market grid (round 3). Every param is an arrival intent the page owns afterwards. */
  market: (o: { q?: string | null; subsector?: string | null; canton?: string | null;
                tier?: string | null; organisation?: number | null } = {}): Dest =>
    build('market', { q: o.q, subsector: o.subsector, canton: o.canton, tier: o.tier, organisation: o.organisation }),

  signals: (): Dest => build('signals', {}),
  campaigns: (): Dest => build('campaigns', {}),
  agents: (o: { tab?: string | null } = {}): Dest => build('agents', { tab: o.tab }),

  /** The Run desk (BUILD-BRIEF-R3f §2) — the third detail route. `id` is the address, and the
   *  page is hidden from the nav: a run is reached from a `RUN-` ref, never from the sidebar. */
  run: (o: { id: number; anchor?: string | null }): Dest => ({
    page: 'run',
    runId: o.id,
    params: o.anchor ? { anchor: o.anchor } : {},
  }),

  admin: (o: { tab?: string | null } = {}): Dest => build('admin', { tab: o.tab }),

  /** The Network page (round 5). Every param is an arrival intent the page owns afterwards. */
  network: (o: { focus?: number | null; role?: string | null; canton?: string | null;
                  regime?: string | null; year?: number | null; group?: 'regime' | null;
                  view?: 'payers' | 'providers' | 'matrix' | null } = {}): Dest =>
    build('network', { focus: o.focus, role: o.role, canton: o.canton, regime: o.regime, year: o.year, group: o.group, view: o.view }),

  /** The Sources page (round 6). `key` is an arrival intent that scrolls to that row and opens
   *  its documents — it never remounts the page (law 1). */
  sources: (o: { key?: string | null } = {}): Dest => build('sources', { key: o.key }),
} as const;

/**
 * Every page a constructor can reach. Compile-time exhaustiveness: the assignment below stops
 * compiling the moment the registry grows a PageId this list does not carry.
 */
export const ROUTED_PAGES = [
  'today', 'review', 'inbox', 'people', 'person', 'orgs', 'org',
  'pipeline', 'search', 'market', 'signals', 'campaigns', 'agents', 'run', 'admin', 'network',
  'sources',
] as const satisfies readonly PageId[];

const _everyPageIsRoutable: (typeof ROUTED_PAGES)[number] extends PageId
  ? PageId extends (typeof ROUTED_PAGES)[number] ? true : never
  : never = true;
void _everyPageIsRoutable;

// ── hash ↔ dest ─────────────────────────────────────────────────────────────

export function toHash(dest: Dest): string {
  const head = dest.page;
  const id = dest.personId ?? dest.organisationId ?? dest.runId;
  // Note what this does NOT emit: a second '#'. An anchor travels as a param and is applied by
  // the receiver — '#people#facts' would parse as a page named "people#facts", i.e. no page.
  const query = new URLSearchParams(id ? { id: String(id) } : {});
  for (const [k, v] of Object.entries(dest.params)) query.set(k, v);
  const q = query.toString();
  return q ? `#${head}?${q}` : `#${head}`;
}

/** For `href` on a real anchor — right-click and open-in-new-tab keep working. */
export const href = (dest: Dest): string => toHash(dest);

/**
 * Parse a raw hash body (no leading '#').
 *
 * `resolvePage` is injected rather than imported so this module stays free of the registry —
 * which imports every page component, and through them React and the whole API layer.
 */
export function parseRoute(raw: string, resolvePage: (head: string) => PageId): Route {
  const [head, query = ''] = raw.split('?');
  const params = new URLSearchParams(query);

  if (head === 'person' || head === 'org' || head === 'run') {
    const id = Number(params.get('id'));
    if (Number.isInteger(id) && id > 0) {
      // Composite key: two subjects are two pages, not one page with a filter. The detail page
      // is hidden from the nav and declares `navOwner`, so People / Organisations / Agents
      // stays lit.
      return {
        page: head,
        personId: head === 'person' ? id : null,
        organisationId: head === 'org' ? id : null,
        runId: head === 'run' ? id : null,
        params,
        key: `${head}:${id}`,
      };
    }
    // A detail route with a missing or garbled id has nothing to show: the list it belongs to
    // is the fallback — People, Organisations, or the Agents panel a run is listed on.
    const list: PageId = head === 'person' ? 'people' : head === 'org' ? 'orgs' : 'agents';
    return { page: list, personId: null, organisationId: null, runId: null, params: new URLSearchParams(), key: list };
  }

  const page = resolvePage(head);
  // Law 1: the key is the page id. Params change; the page does not remount.
  return { page, personId: null, organisationId: null, runId: null, params, key: page };
}

// ── arriving ────────────────────────────────────────────────────────────────

const EMPTY_PARAMS = new URLSearchParams();
export const RouteParamsContext = createContext<URLSearchParams>(EMPTY_PARAMS);
export const useRouteParams = (): URLSearchParams => useContext(RouteParamsContext);

export type DeepLinkHandlers = Record<string, (value: string) => void>;

/**
 * Law 2, in one hook. A page declares which arrival intents it honours; everything else on the
 * query string is ignored. After the handlers run the query is cleared with `replaceState` —
 * which does NOT fire `hashchange`, so the page keeps the state its handlers just set.
 */
export function useDeepLink(params: URLSearchParams, handlers: DeepLinkHandlers): void {
  const applied = useRef<string | null>(null);
  const query = params.toString();
  // Every honest caller re-creates the handler table on each render; freezing it in a ref keeps
  // this effect keyed on the QUERY alone, which is what "one-shot" means.
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    if (!query || applied.current === query) return;
    applied.current = query;
    const arrived = new URLSearchParams(query);
    for (const [key, value] of arrived) {
      const handler = latest.current[key];
      if (handler) handler(value);
      else if (key === 'anchor') scrollToAnchor(value);
    }
    const [head] = window.location.hash.slice(1).split('?');
    // Keep a detail route addressable: its `id` is the address, not an intent.
    const keep = (head === 'person' || head === 'org' || head === 'run') && arrived.get('id')
      ? `?id=${arrived.get('id')}`
      : '';
    window.history.replaceState(null, '', `#${head}${keep}`);
  }, [query]);
}

/** Anchors arrive with or without the '#'; both forms are accepted. */
export function scrollToAnchor(anchor: string): void {
  if (!anchor) return;
  const id = anchor.startsWith('#') ? anchor.slice(1) : anchor;
  // The receiver has just rendered from a state update; wait one frame for it to exist.
  requestAnimationFrame(() => {
    const node = document.getElementById(id) ?? document.querySelector(`[data-anchor="${id}"]`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/** Navigate. The one place a hash is assigned. */
export function open(dest: Dest): void {
  window.location.hash = toHash(dest);
}
